import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Layers, AlertTriangle, Trash2, Pencil, Eye } from 'lucide-react';

import DashboardLayout from '../components/layout/DashboardLayout';
import { EmptyState } from '../components/shared/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import {
  API,
  bucketLabel,
  getStatusBadgeClass,
} from '../lib/componentsModule';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
} from 'recharts';

const CHART_COLORS = ['#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316'];

export default function ComponentsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'superadmin' || user?.role === 'platform_admin';

  const [organizations, setOrganizations] = useState([]);
  const [assets, setAssets] = useState([]);
  const [components, setComponents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [bucketTab, setBucketTab] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(user?.org_id || 'all');
  const [selectedAsset, setSelectedAsset] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    axios
      .get(`${API}/organizations`)
      .then((res) => setOrganizations(res.data || []))
      .catch(() => setOrganizations([]));
  }, [isAdmin]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.append('limit', '2000');
      if (bucketTab !== 'all' && bucketTab !== 'insights') params.append('bucket', bucketTab);
      if (selectedOrg !== 'all') params.append('org_id', selectedOrg);
      if (selectedAsset !== 'all') params.append('asset_id', selectedAsset);
      if (bucketTab !== 'insights' && search.trim()) params.append('search', search.trim());

      const assetParams = new URLSearchParams();
      if (selectedOrg !== 'all') assetParams.append('org_id', selectedOrg);

      const summaryParams = new URLSearchParams();
      if (selectedOrg !== 'all') summaryParams.append('org_id', selectedOrg);
      if (selectedAsset !== 'all') summaryParams.append('asset_id', selectedAsset);

      const [componentsRes, assetsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/components?${params.toString()}`),
        axios.get(`${API}/assets?${assetParams.toString()}`),
        axios.get(`${API}/components/analytics/summary?${summaryParams.toString()}`),
      ]);

      setComponents(componentsRes.data?.items || []);
      setAssets(assetsRes.data || []);
      setSummary(summaryRes.data || null);
    } catch (error) {
      toast.error('Failed to load components');
    } finally {
      setLoading(false);
    }
  }, [bucketTab, selectedOrg, selectedAsset, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredComponents = useMemo(() => {
    return components;
  }, [components]);

  const bucketDistribution = useMemo(() => {
    const grouped = filteredComponents.reduce((acc, component) => {
      const label = bucketLabel(component.bucket);
      acc[label] = (acc[label] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(grouped).map(([name, value]) => ({ name, value }));
  }, [filteredComponents]);

  const manufacturerDistribution = useMemo(() => {
    const grouped = filteredComponents.reduce((acc, component) => {
      const key = component.manufacturer || 'Unspecified';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(grouped)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [filteredComponents]);

  const statusDistribution = useMemo(() => {
    const grouped = filteredComponents.reduce((acc, component) => {
      const status = component.status || 'no_status';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(grouped)
      .map(([name, value]) => ({ name: name.replaceAll('_', ' '), value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredComponents]);

  const spareLowByManufacturer = useMemo(() => {
    const grouped = filteredComponents
      .filter((component) => (
        component.bucket === 'spare'
        && component.safety_quantity !== null
        && component.safety_quantity !== undefined
        && Number(component.quantity || 0) <= Number(component.safety_quantity || 0)
      ))
      .reduce((acc, component) => {
        const key = component.manufacturer || 'Unspecified';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
    return Object.entries(grouped)
      .map(([name, lowStockCount]) => ({ name, lowStockCount }))
      .sort((a, b) => b.lowStockCount - a.lowStockCount)
      .slice(0, 10);
  }, [filteredComponents]);

  const topManufacturer = manufacturerDistribution[0];
  const topManufacturerShare = topManufacturer
    ? Math.round((topManufacturer.value / Math.max(filteredComponents.length, 1)) * 100)
    : 0;

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      setIsDeleting(true);
      await axios.delete(`${API}/components/${deleteTarget.id}`);
      toast.success('Component deleted');
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete component');
    } finally {
      setIsDeleting(false);
    }
  };

  const getAssetName = (assetId) => assets.find((asset) => asset.id === assetId)?.name || 'Unlinked asset';

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Components</h1>
            <p className="text-slate-500">Operational, spare, and scrap/recyclable component records</p>
          </div>
          <Button onClick={() => navigate('/components/new')}>
            <Plus className="w-4 h-4 mr-2" />
            Add Component
          </Button>
        </div>

        <Tabs value={bucketTab} onValueChange={setBucketTab}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="operational">Operational</TabsTrigger>
            <TabsTrigger value="spare">Spare</TabsTrigger>
            <TabsTrigger value="scrap_recyclable">Scrap / Recyclable</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
          </TabsList>

          <TabsContent value={bucketTab} className="space-y-6">
            {bucketTab !== 'insights' && (
              <>
                <Card>
                  <CardContent className="p-4 flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1 max-w-md">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name, serial, manufacturer"
                        className="pl-9"
                      />
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                      {isAdmin && (
                        <Select value={selectedOrg} onValueChange={(value) => { setSelectedOrg(value); setSelectedAsset('all'); }}>
                          <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="All organizations" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All organizations</SelectItem>
                            {organizations.map((org) => (
                              <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      <Select value={selectedAsset} onValueChange={setSelectedAsset}>
                        <SelectTrigger className="w-[220px]">
                          <SelectValue placeholder="All assets" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All assets</SelectItem>
                          {assets.map((asset) => (
                            <SelectItem key={asset.id} value={asset.id}>{asset.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {loading ? (
                  <div className="flex items-center justify-center h-40">
                    <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
                  </div>
                ) : filteredComponents.length === 0 ? (
                  <EmptyState
                    icon={Layers}
                    title="No components found"
                    description="Start by adding your first component"
                    actionLabel="Add Component"
                    onAction={() => navigate('/components/new')}
                  />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredComponents.map((component) => {
                      const isSpareLow = component.bucket === 'spare'
                        && component.safety_quantity !== null
                        && component.safety_quantity !== undefined
                        && Number(component.quantity || 0) <= Number(component.safety_quantity || 0);

                      return (
                        <Card key={component.id} className={isSpareLow ? 'border-amber-200' : ''}>
                          <CardHeader className="pb-2">
                            <div className="flex justify-between items-start gap-2">
                              <div>
                                <CardTitle className="text-lg">{component.name}</CardTitle>
                                <p className="text-xs text-slate-500 mt-1">{bucketLabel(component.bucket)}</p>
                              </div>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate(`/components/${component.id}`)}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate(`/components/${component.id}/edit`)}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-red-500 hover:text-red-600"
                                  onClick={() => setDeleteTarget({ id: component.id, name: component.name })}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3 text-sm">
                            {component.status && (
                              <Badge variant="outline" className={getStatusBadgeClass(component.bucket, component.status)}>
                                {component.status.replaceAll('_', ' ')}
                              </Badge>
                            )}

                            {isSpareLow && (
                              <div className="text-amber-700 text-xs flex items-center gap-1">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                Safety quantity threshold reached
                              </div>
                            )}

                            <div className="grid grid-cols-2 gap-2">
                              <div className="rounded-md bg-slate-50 p-2">
                                <p className="text-slate-500 text-xs">Quantity</p>
                                <p className="font-semibold">{Number(component.quantity || 0).toLocaleString()} {component.unit}</p>
                              </div>
                              <div className="rounded-md bg-slate-50 p-2">
                                <p className="text-slate-500 text-xs">Type</p>
                                <p className="font-semibold capitalize">{(component.type || 'n/a').replaceAll('_', ' ')}</p>
                              </div>
                            </div>

                            <div>
                              <p className="text-slate-500 text-xs">Asset</p>
                              <p className="font-medium">{getAssetName(component.asset_id)}</p>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs">Serial Number</p>
                              <p className="font-medium">{component.serial_number || 'N/A'}</p>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {bucketTab === 'insights' && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <Card>
                  <CardHeader><CardTitle>Total Components</CardTitle></CardHeader>
                  <CardContent><p className="text-3xl font-bold">{summary?.totals?.all || 0}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle>Operational</CardTitle></CardHeader>
                  <CardContent><p className="text-3xl font-bold">{summary?.totals?.operational || 0}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle>Spare</CardTitle></CardHeader>
                  <CardContent><p className="text-3xl font-bold">{summary?.totals?.spare || 0}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle>Scrap / Recyclable</CardTitle></CardHeader>
                  <CardContent><p className="text-3xl font-bold">{summary?.totals?.scrap_recyclable || 0}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle>Low Stock (Spare)</CardTitle></CardHeader>
                  <CardContent><p className="text-3xl font-bold text-amber-700">{summary?.alerts?.low_stock_count || 0}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle>Overdue PM</CardTitle></CardHeader>
                  <CardContent><p className="text-3xl font-bold text-red-700">{summary?.alerts?.overdue_pm_count || 0}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle>Top Manufacturer</CardTitle></CardHeader>
                  <CardContent>
                    {topManufacturer ? (
                      <>
                        <p className="text-xl font-semibold text-slate-900">{topManufacturer.name}</p>
                        <p className="text-slate-500">{topManufacturer.value} components ({topManufacturerShare}% share)</p>
                      </>
                    ) : (
                      <p className="text-slate-500">No data</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="md:col-span-2 xl:col-span-1">
                  <CardHeader><CardTitle>Component Mix by Bucket</CardTitle></CardHeader>
                  <CardContent className="h-72">
                    {bucketDistribution.length === 0 ? (
                      <p className="text-slate-500">No data available.</p>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={bucketDistribution} dataKey="value" nameKey="name" outerRadius={95} label>
                            {bucketDistribution.map((entry, index) => (
                              <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>

                <Card className="md:col-span-2 xl:col-span-2">
                  <CardHeader><CardTitle>Top Manufacturers by Component Count</CardTitle></CardHeader>
                  <CardContent className="h-72">
                    {manufacturerDistribution.length === 0 ? (
                      <p className="text-slate-500">No data available.</p>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={manufacturerDistribution} layout="vertical" margin={{ left: 20, right: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" allowDecimals={false} />
                          <YAxis type="category" dataKey="name" width={140} />
                          <Tooltip />
                          <Bar dataKey="value" fill="#0ea5e9" radius={[0, 6, 6, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>

                <Card className="md:col-span-2 xl:col-span-1">
                  <CardHeader><CardTitle>Status Distribution</CardTitle></CardHeader>
                  <CardContent className="h-72">
                    {statusDistribution.length === 0 ? (
                      <p className="text-slate-500">No status data available.</p>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={statusDistribution} dataKey="value" nameKey="name" outerRadius={95} label>
                            {statusDistribution.map((entry, index) => (
                              <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>

                <Card className="md:col-span-2 xl:col-span-2">
                  <CardHeader><CardTitle>Low-Stock Spare Components by Manufacturer</CardTitle></CardHeader>
                  <CardContent className="h-72">
                    {spareLowByManufacturer.length === 0 ? (
                      <p className="text-slate-500">No low-stock spare components for current filters.</p>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={spareLowByManufacturer}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} height={70} />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Bar dataKey="lowStockCount" fill="#f59e0b" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>

                <Card className="md:col-span-2 xl:col-span-3">
                  <CardHeader><CardTitle>Recycled Amount by Currency</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {Object.keys(summary?.recycled_amount_by_currency || {}).length === 0 ? (
                      <p className="text-slate-500">No recycled amount recorded yet.</p>
                    ) : (
                      Object.entries(summary?.recycled_amount_by_currency || {}).map(([currency, amount]) => (
                        <div key={currency} className="flex justify-between text-sm border-b pb-2">
                          <span>{currency}</span>
                          <span className="font-semibold">{Number(amount).toLocaleString()}</span>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => {
          if (!open && !isDeleting) {
            setDeleteTarget(null);
          }
        }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Component</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently remove
                {deleteTarget?.name ? ` "${deleteTarget.name}"` : ' this component'}
                . This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                onClick={(e) => {
                  e.preventDefault();
                  handleDelete();
                }}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
